import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Fragment, lazy, Suspense, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import Checkbox from '@atlaskit/checkbox';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagGroup } from '@atlaskit/flag';
import InfoIcon from '@atlaskit/icon/utility/migration/information--info';
import { RovoIcon } from '@atlaskit/legacy-custom-icons';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import AtlassianIntelligenceIcon from '../../atlassian-intelligence-icon';
import { OpenChatButton } from '../../common';
import { ChatFollowupInput } from '../../common/ui/open-chat/chat-followup-input';
import { AiBrand, ATLASSIAN_INTELLIGENCE_TRUST_LINK, ATLASSIAN_ROVO_LINK, CardTemplate, FEEDBACK_CONTEXT_CF, FEEDBACK_ENTRYPOINT_ID_SAIN, SAC_ATLASSIAN_INTELLIGENCE_TRUST_LINK } from '../../constants';
import { messages } from '../../messages';
import { DefinitionFooter } from '../../v2/definitions/footer/definition-footer';
import { AIAnswerWhoFooter } from '../ai-answer-body/ai-answer-result';
import { useAIAnswerContext } from '../ai-answer-context';
import { NLPSearchResultFormat } from '../types';
import { FeedbackButtons } from './feedback-buttons';
import { FollowUpSuggestionFooter } from './followup-suggestion-footer';
import { AIAnswerContentBrandWrapper, AIAnswerFeedbackWrapper, AIAnswerFooterInformationContentVary, AIAnswerFooterInformationContentVaryLinkText, AIAnswerFooterPoweredBy, AIAnswerFooterPoweredByAIWrapper, AIAnswerFooterWrapper, AIAnswerReportDescriptionWrapper } from './styled';
var AkFeedbackCollector = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-collector" */'@atlaskit/feedback-collector').then(function (module) {
    return {
      default: module.default
    };
  });
});
var AkFeedbackFlag = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-flag" */'@atlaskit/feedback-collector').then(function (_ref) {
    var FeedbackFlag = _ref.FeedbackFlag;
    return {
      default: FeedbackFlag
    };
  });
});
var chatInputContainerStyles = xcss({
  width: '100%'
});
var ExperimentCohort = /*#__PURE__*/function (ExperimentCohort) {
  ExperimentCohort["CONTROL"] = "control";
  ExperimentCohort["DEFAULT"] = "default";
  ExperimentCohort["TEST"] = "test";
  return ExperimentCohort;
}(ExperimentCohort || {});
export var AIAnswerContentWaryAndBrandSection = function AIAnswerContentWaryAndBrandSection() {
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    brand = _useAIAnswerContext.brand,
    cardTemplate = _useAIAnswerContext.cardTemplate,
    sourceProduct = _useAIAnswerContext.sourceProduct;
  return /*#__PURE__*/React.createElement(AIAnswerContentBrandWrapper, null, /*#__PURE__*/React.createElement(AIAnswerFooterInformationContentVary, null, /*#__PURE__*/React.createElement(InfoIcon, {
    label: "",
    color: "var(--ds-icon-subtle, #626F86)",
    LEGACY_size: "small"
  }), /*#__PURE__*/React.createElement(AIAnswerFooterInformationContentVaryLinkText, null, /*#__PURE__*/React.createElement(Link, {
    rel: "noopener noreferrer",
    appearance: "subtle",
    href: sourceProduct === 'sac' ? SAC_ATLASSIAN_INTELLIGENCE_TRUST_LINK : ATLASSIAN_INTELLIGENCE_TRUST_LINK
  }, formatMessage(cardTemplate === CardTemplate.RIGHT_PANEL ? messages.ai_answer_dialog_footer_content_quality_short : messages.ai_answer_dialog_footer_content_quality_long)))), /*#__PURE__*/React.createElement(AIAnswerFooterPoweredBy, null, brand === AiBrand.ROVO ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
    rel: "noopener noreferrer",
    appearance: "subtle",
    href: ATLASSIAN_ROVO_LINK
  }, "Rovo"), /*#__PURE__*/React.createElement(RovoIcon, {
    label: "",
    primaryColor: "var(--ds-icon-subtle, #626F86)"
  })) : /*#__PURE__*/React.createElement(AIAnswerFooterPoweredByAIWrapper, null, formatMessage(messages.ai_answer_dialog_loaded_footer), /*#__PURE__*/React.createElement(AtlassianIntelligenceIcon, {
    greyed: true,
    size: 'large'
  }))));
};
export var SmartAnswerFooterSection = function SmartAnswerFooterSection(_ref2) {
  var showOpenChat = _ref2.showOpenChat;
  var _useAIAnswerContext2 = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext2.commonAttributes;
  var query = commonAttributes.query,
    answerString = commonAttributes.answerString;
  var experimentCohort = fg('kd_sain_chat_interaction_exp_gate') ? FeatureGates.getExperimentValue('kd_sain_chat_interaction_exp', 'cohort', ExperimentCohort.DEFAULT) : ExperimentCohort.DEFAULT;
  return /*#__PURE__*/React.createElement(AIAnswerFooterWrapper, null, showOpenChat && (experimentCohort === ExperimentCohort.TEST ? /*#__PURE__*/React.createElement(Box, {
    xcss: chatInputContainerStyles
  }, /*#__PURE__*/React.createElement(ChatFollowupInput, {
    query: query,
    answerString: answerString,
    source: 'sain',
    experimentCohort: experimentCohort
  })) : /*#__PURE__*/React.createElement(OpenChatButton, {
    prompt: query,
    query: query,
    answerString: answerString,
    source: 'sain',
    experimentCohort: experimentCohort
  })), /*#__PURE__*/React.createElement(FollowUpSuggestionFooter, null));
};
var AIAnswerBaseFooter = function AIAnswerBaseFooter(_ref3) {
  var showWhoAnswer = _ref3.showWhoAnswer,
    showOpenChat = _ref3.showOpenChat;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage,
    locale = _useIntl2.locale;
  var _useAIAnswerContext3 = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext3.commonAttributes,
    _useAIAnswerContext3$ = _useAIAnswerContext3.userDetails,
    name = _useAIAnswerContext3$.name,
    id = _useAIAnswerContext3$.id,
    readingAidsOptions = _useAIAnswerContext3.readingAidsOptions,
    loading = _useAIAnswerContext3.loading,
    brand = _useAIAnswerContext3.brand,
    isAnswered = _useAIAnswerContext3.isAnswered;
  var errorState = commonAttributes.errorState,
    answerFormat = commonAttributes.answerFormat,
    followUps = commonAttributes.followUps;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showFeedbackModal = _useState2[0],
    setFeedbackModal = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    displayFlag = _useState4[0],
    setDisplayFlag = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hasConsent = _useState6[0],
    setConsent = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    topicsNotRelevant = _useState8[0],
    setTopicsNotRelevant = _useState8[1];
  var feedbackContextObject = {
    searchSessionId: commonAttributes.searchSessionId,
    source: commonAttributes.source,
    queryHash: commonAttributes.queryHash,
    cloudId: commonAttributes.cloudId,
    prompt: hasConsent ? commonAttributes.query : undefined,
    answer: hasConsent ? commonAttributes.answerString : undefined,
    documentId: readingAidsOptions === null || readingAidsOptions === void 0 ? void 0 : readingAidsOptions.contentARI,
    errorState: errorState,
    topicsNotRelevant: topicsNotRelevant ? true : undefined
  };
  var feedbackContext = Object.entries(feedbackContextObject).map(function (_ref4) {
    var _ref5 = _slicedToArray(_ref4, 2),
      key = _ref5[0],
      value = _ref5[1];
    return "".concat(key, ": ").concat(value);
  }).join(', ');
  var toggleConsentCheckbox = useCallback(function () {
    setConsent(!hasConsent);
  }, [hasConsent]);
  var toggleTopicsNotRelevantCheckbox = useCallback(function () {
    setTopicsNotRelevant(!topicsNotRelevant);
  }, [topicsNotRelevant]);
  var topicsEnabled = fg('platform_search-ai_related-entities');
  var brandName = brand === AiBrand.ROVO ? 'Rovo' : 'Atlassian Intelligence';
  var renderOpenChatButton = showOpenChat && !loading && isAnswered && fg('kd_smart_answers_open_chat');
  return /*#__PURE__*/React.createElement(Fragment, null, showWhoAnswer && answerFormat === NLPSearchResultFormat.JSON && /*#__PURE__*/React.createElement(AIAnswerWhoFooter, null), !loading && /*#__PURE__*/React.createElement(AIAnswerFeedbackWrapper, {
    isCompact: true
  }, /*#__PURE__*/React.createElement(FeedbackButtons, {
    setDisplayFlag: setDisplayFlag,
    setFeedbackModal: setFeedbackModal
  })), /*#__PURE__*/React.createElement(AIAnswerContentWaryAndBrandSection, null), (renderOpenChatButton || followUps.length > 0) && /*#__PURE__*/React.createElement(SmartAnswerFooterSection, {
    showOpenChat: renderOpenChatButton
  }), showFeedbackModal ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(AkFeedbackCollector, {
    locale: locale,
    onClose: function onClose() {
      return setFeedbackModal(false);
    },
    onSubmit: function onSubmit() {
      setDisplayFlag(true);
    },
    customContent: /*#__PURE__*/React.createElement(Stack, {
      space: "space.050"
    }, /*#__PURE__*/React.createElement(Checkbox, {
      label: formatMessage(messages.ai_answer_component_report_consent_checkbox_label),
      onChange: toggleConsentCheckbox,
      isChecked: hasConsent
    }), topicsEnabled && /*#__PURE__*/React.createElement(Checkbox, {
      label: formatMessage(messages.ai_answer_component_topics_not_relevant_checkbox_label),
      onChange: toggleTopicsNotRelevantCheckbox,
      isChecked: topicsNotRelevant
    })),
    atlassianAccountId: id,
    name: name,
    entrypointId: FEEDBACK_ENTRYPOINT_ID_SAIN,
    additionalFields: [{
      id: FEEDBACK_CONTEXT_CF,
      value: feedbackContext
    }],
    feedbackTitle: formatMessage(messages.ai_answer_component_report_modal_title, {
      brandName: brandName
    }),
    feedbackTitleDetails: /*#__PURE__*/React.createElement(AIAnswerReportDescriptionWrapper, null, formatMessage(messages.ai_answer_component_report_summary_label)),
    feedbackGroupLabels: {
      empty: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_empty_option_share_label)
      },
      question: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_relevance_option_label)
      },
      comment: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_inaccurate_option_label)
      },
      bug: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_offensive_option_label)
      },
      suggestion: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_other_option_label)
      }
    },
    summaryPlaceholder: formatMessage(messages.ai_answer_component_summary_placeholder_label)
  })) : null, displayFlag && /*#__PURE__*/React.createElement(FlagGroup, {
    onDismissed: function onDismissed() {
      return setDisplayFlag(false);
    }
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(AkFeedbackFlag, {
    locale: locale,
    description: formatMessage(messages.ai_answer_component_feedback_flag_description)
  }))));
};
export var SearchPageSmartAnswerFooter = function SearchPageSmartAnswerFooter() {
  return /*#__PURE__*/React.createElement(AIAnswerBaseFooter, {
    showOpenChat: true
  });
};
var AIAnswerDialogFooter = function AIAnswerDialogFooter() {
  return /*#__PURE__*/React.createElement(AIAnswerBaseFooter, {
    showWhoAnswer: true
  });
};
export var AIAnswerFooter = function AIAnswerFooter() {
  var _useAIAnswerContext4 = useAIAnswerContext(),
    isReadingAids = _useAIAnswerContext4.isReadingAids,
    cardTemplate = _useAIAnswerContext4.cardTemplate;
  switch (cardTemplate) {
    case CardTemplate.SMART_ANSWER:
      return /*#__PURE__*/React.createElement(SearchPageSmartAnswerFooter, null);
    default:
      return isReadingAids ? /*#__PURE__*/React.createElement(DefinitionFooter, null) : /*#__PURE__*/React.createElement(AIAnswerDialogFooter, null);
  }
};